import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { cloneDeep, debounce } from 'lodash';
import { getAuthState } from '../../store/selectors';
import BoxWithShadow from './basic-style-components/BoxWithShadow';
import poweredBy from '../../ASUPhotos/logos/poweredByBlackAndWhite.png';
import FiltersSection from './FilterSection';
import { usersCopy } from '../../copy/advisor-dashboard/advisor-dashboard-base.json';
import Loading from '../utility/Loading';
import AssignAdvisors from './AssignAdvisors';
import Spyglass from '../utility/Spyglass';
import {
  getAllUsersForAdvisor,
  updateUsersTabFilters,
  setUserNameSearchString,
  getUserDashboardInfo,
  resetUsersTabPages,
  getRolePermissions,
} from '../../store/actions';
import Pagination from './Pagination';

export class Users extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      filtersOpen: false,
      usersLoading: false,
      displayUsers: false,
      currentPage: 1,
      searchString: '',
      loading: true,
    };
  }

  componentDidMount = async () => {
    this._isMounted = true;
    this.setState({
      loading: true,
    });
    await this.props.getRolePermissions(this.props.auth.jwtToken);
    this.setState({
      loading: false,
    });
  };

  handleGetUsers = async (setLoading = true) => {
    const { currentPage } = this.state;
    if (this._isMounted) {
      if (setLoading) {
        this.setState({
          usersLoading: true,
        });
      }
      await this.props.getAllUsersForAdvisor(
        this.props.tenant.id,
        currentPage - 1,
        true
      );
      if (setLoading) {
        this.setState({
          usersLoading: false,
        });
      }
    }
  };

  componentWillUnmount = () => {
    this.props.resetUsersTabPages();
    this._isMounted = false;
  };

  colorPrimary = {
    color: `${this.props.tenant.primary}`,
  };

  borderBottomSecondary = (color) => {
    return { borderBottom: `4px solid ${color}` };
  };

  backgroundPrimary = {
    backgroundColor: `${this.props.tenant.primary}`,
  };

  updateFiltersOpen = () => {
    const { filtersOpen } = this.state;
    if (this._isMounted) {
      this.setState({
        filtersOpen: !filtersOpen,
      });
    }
  };

  formatList = (options) => {
    return options.map((x) => {
      return { titleEn: x, checked: false };
    });
  };

  clearAllFilters = () => {
    const clone = cloneDeep(this.props.admin.permissions.usersFilters);
    const cleared = {};
    Object.entries(clone).forEach(([key, value]) => {
      if (value === null) {
        cleared[key] = null;
        return;
      }
      cleared[key] = [];
      return value.forEach((filter) => {
        if (!filter.id) {
          cleared[key].push({
            titleEn: filter.titleEn,
            checked: false,
          });
        } else {
          cleared[key].push({
            id: filter.id,
            titleEn: filter.titleEn,
            checked: false,
          });
        }
      });
    });
    this.props.updateUsersTabFilters(cleared);
  };

  switchDisplayUsersStateVal = () => {
    if (this._isMounted) {
      this.setState({
        displayUsers: true,
      });
    }
  };

  displayFilters = () => {
    const { filtersOpen, displayUsers } = this.state;
    const { usersFilters } = this.props.admin.permissions;
    return (
      <div className="advisor-dash-users__filters-section">
        <BoxWithShadow>
          <button
            type="button"
            onClick={this.updateFiltersOpen}
            onKeyDown={(e) => {
              if (['Enter'].includes(e.key)) this.updateFiltersOpen();
            }}
            className="advisor-dash-users__filters-open-button"
          >
            <div className="advisor-dash-users__filters-header">
              <p>{usersCopy.filtersHeader}</p>
              <p className="advisor-dash-users__filters-open-close">
                {filtersOpen ? `- Close` : `+ Open`}
              </p>
            </div>
          </button>
          {filtersOpen && (
            <>
              <div className="advisor-dash-users__filters-container">
                {Object.keys(usersFilters).map((x) => {
                  return (
                    usersFilters[x] && (
                      <FiltersSection
                        key={x}
                        filterTitle={x}
                        filterOptions={usersFilters[x]}
                        listName={x}
                      />
                    )
                  );
                })}
              </div>
              <div className="advisor-dash-users__submit-button-container">
                <button
                  style={this.backgroundPrimary}
                  className="button--primary"
                  type="button"
                  onClick={() => {
                    if (!displayUsers) {
                      this.switchDisplayUsersStateVal();
                    }
                    this.handlePageChange();
                  }}
                  onKeyDown={(e) => {
                    if (['Enter'].includes(e.key))
                      if (!displayUsers) {
                        this.switchDisplayUsersStateVal();
                      }
                    this.handlePageChange();
                  }}
                  tabIndex={0}
                >
                  {usersCopy.applyButtonText}
                </button>
                <button
                  className="advisor-dash-users__clear-all"
                  type="button"
                  onClick={() => {
                    this.clearAllFilters();
                  }}
                  onKeyDown={(e) => {
                    if (['Enter'].includes(e.key)) this.clearAllFilters();
                  }}
                  tabIndex={0}
                  style={this.colorPrimary}
                >
                  {usersCopy.clearAllButtonText}
                </button>
              </div>
            </>
          )}
        </BoxWithShadow>
      </div>
    );
  };

  navigateToUserProfile = async (userProfileId, userTenantId) => {
    this.props.history.push({
      pathname: `user-profile`,
      search: `?userId=${userProfileId}&tenantId=${userTenantId}`,
    });
  };

  displayUsers = (user) => {
    return (
      <div
        className="advisor-dash-users__user-tile"
        key={`${user.id}_${user.tenantId}`}
        style={this.borderBottomSecondary(user.tenantSecondary)}
      >
        {/* We may be using this again in the future, so just commenting it out for now */}
        {/* <input
          type="checkbox"
          className="advisor-dash-users__checkbox"
          style={this.colorPrimary}
          id={`${user.id}_${user.tenantId}`}
          onChange={() => {
            // we'll presumably use this once we incorporate group actions to the users tab - for now it does nothing.
          }}
        /> */}
        <div className="advisor-dash-users__name-container">
          <p className="advisor-dash-users__bold-heading advisor-dash-users__user-name">{`${user.firstName} ${user.lastName}`}</p>
          <p className="advisor-dash-users__high-school">{user.highSchool}</p>
          <button
            className="advisor-dash-users__view-more"
            onClick={() => {
              this.navigateToUserProfile(user.id, user.tenantId);
            }}
            onKeyPress={() => {
              this.navigateToUserProfile(user.id, user.tenantId);
            }}
            type="button"
            style={this.colorPrimary}
          >
            View Profile
          </button>
        </div>
        <div className="advisor-dash-users__advisor-container">
          <AssignAdvisors
            tenant={this.props.tenant}
            user={user}
            handleGetUsers={this.handleGetUsers}
            key={`${user.id}_${user.tenantId}`}
            highSchool={user.highSchool}
          />
        </div>
        <div className="advisor-dash-users__user-info-container">
          <div className="advisor-dash-users__row">
            <p className="advisor-dash-users__bold-heading">Email: </p>
            <p className="advisor-dash-users__light-heading">{user.email}</p>
          </div>
          <div className="advisor-dash-users__row">
            <p className="advisor-dash-users__bold-heading">DOB:</p>
            <p className="advisor-dash-users__light-heading">
              {moment(user.dateOfBirth).format('MM/DD/YYYY')}
            </p>
          </div>
          <div className="advisor-dash-users__row">
            <p className="advisor-dash-users__bold-heading">Grad Year:</p>
            <p className="advisor-dash-users__light-heading">
              {user.graduationYear}
            </p>
          </div>
        </div>
      </div>
    );
  };

  handleSearchByName = debounce(async () => {
    const { searchString } = this.state;
    this.setState({
      currentPage: 1,
    });
    await this.props.setUserNameSearchString(searchString);
    await this.props.getAllUsersForAdvisor(
      this.props.tenant.id,
      this.state.currentPage - 1,
      true
    );
    if (!this.state.displayUsers) {
      this.switchDisplayUsersStateVal();
    }
    this.setState({ usersLoading: false });
  }, 500);

  handlePageChange = async (pageNumber = 1) => {
    if (pageNumber !== this.state.currentPage) {
      await this.setState({
        currentPage: pageNumber,
      });
    }
    this.handleGetUsers();
  };

  render() {
    const { filtersOpen, displayUsers, loading } = this.state;
    return loading ? (
      <Loading height="100%" />
    ) : (
      <div
        style={this.props.styling.mainContent}
        className="advisor-dash-users"
      >
        <header className="advisor-dash-users__header">
          <p>{usersCopy.searchHeader}</p>
          <div
            className="advisor-dash-users__header-buttons"
            style={{ visibility: 'hidden' }}
          >
            <button type="button">{usersCopy.copyAdvisorLink}</button>
            <button type="button">{usersCopy.inviteUser}</button>
          </div>
        </header>
        <div className="advisor-dash-users__searchbar-container">
          <input
            className="advisor-dash-users__searchbar"
            placeholder="Search by Name"
            value={this.state.searchString}
            onChange={(e) => {
              this.setState({
                searchString: e.target.value,
                usersLoading: true,
              });
              this.handleSearchByName();
            }}
          />
          <Spyglass
            color="black"
            overRideStylesMag="advisor-dash-users__spyglass"
          />
        </div>
        {this.displayFilters()}
        <Pagination
          handlePageChange={this.handlePageChange}
          currentPage={this.state.currentPage}
          resultPages={this.props.admin.resultPages}
          primaryColor={this.props.tenant.primary}
        />
        {displayUsers &&
          !this.state.usersLoading &&
          this.props.users.length === 0 && (
            <div className="advisor-dash-users__instructions-box">
              <BoxWithShadow>
                <p className="advisor-dash-users__no-results">
                  {usersCopy.noResults}
                </p>
                <p className="advisor-dash-users__instructions">
                  {usersCopy.searchFilterInstructions}
                </p>
              </BoxWithShadow>
            </div>
          )}

        {displayUsers &&
          !this.state.usersLoading &&
          this.props.users.map((user) => this.displayUsers(user))}

        {displayUsers && this.state.usersLoading && <Loading height="100%" />}

        {displayUsers && (
          <Pagination
            handlePageChange={this.handlePageChange}
            currentPage={this.state.currentPage}
            resultPages={this.props.admin.resultPages}
            primaryColor={this.props.tenant.primary}
          />
        )}

        {!filtersOpen && !displayUsers && (
          <div className="advisor-dash-users__instructions-box">
            <BoxWithShadow>
              <p className="advisor-dash-users__instructions">
                {usersCopy.searchFilterInstructions}
              </p>
            </BoxWithShadow>
          </div>
        )}

        <a
          className="advisor-dash-users__powered-by"
          href="https://www.asu.edu/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={poweredBy} alt="Powered By Arizona State University" />
        </a>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const auth = getAuthState(state);
  const users = state.admin.users;
  const admin = state.admin;
  return { auth, admin, users };
}

export default withRouter(
  connect(mapStateToProps, {
    updateUsersTabFilters,
    getAllUsersForAdvisor,
    getUserDashboardInfo,
    setUserNameSearchString,
    resetUsersTabPages,
    getRolePermissions,
  })(Users)
);
